import React, { FC, ReactNode } from 'react';

import { LoaderTypes } from 'types/app';
import {
  ClientInvitationsClientList,
  GetClientInvitationsByClientComponentProps,
  useGetClientInvitationsByClientQuery,
} from 'api';
import { QueryManager } from 'components';
import { useNotify } from 'hooks';

interface IGetClientInvitationsByClientOptions {
  onLoadMore: () => void;
  loading: boolean;
}

interface IProps {
  loader: LoaderTypes;
  children: (
    data: ClientInvitationsClientList,
    options: IGetClientInvitationsByClientOptions,
  ) => ReactNode;
}

type CombinedProps = IProps & Omit<GetClientInvitationsByClientComponentProps, 'children'>;

const GetClientInvitationsByClient: FC<CombinedProps> = props => {
  const { children, loader, variables, ...queryProps } = props;
  const notify = useNotify();

  const queryResult = useGetClientInvitationsByClientQuery({
    variables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: notify.queryError,
    ...queryProps,
  });

  const { loading, error, data, fetchMore } = queryResult;
  const currentResult = data?.getClientInvitationsByClient ?? null;

  function handleLoadMore() {
    if (loading || error || !currentResult.paginate) {
      return Promise.resolve();
    }

    return fetchMore({
      variables: {
        id: currentResult.id,
        limit: variables.limit,
        created_at: currentResult.created_at,
        invitation_status: currentResult.invitation_status,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        const prevResult = prev.getClientInvitationsByClient;
        const nextResult = fetchMoreResult.getClientInvitationsByClient;
        const updatedResult = {
          ...prevResult,
          ...nextResult,
          client_invitations: [...prevResult.client_invitations, ...nextResult.client_invitations],
        };

        return { ...prev, getClientInvitationsByClient: updatedResult };
      },
    });
  }

  return (
    <QueryManager loader={loader} {...queryResult}>
      {() => {
        if (!data?.getClientInvitationsByClient) {
          return null;
        }

        return children(data.getClientInvitationsByClient, {
          loading,
          onLoadMore: handleLoadMore,
        });
      }}
    </QueryManager>
  );
};

export default GetClientInvitationsByClient;
